// src/components/GatheringCard.tsx
import React, { useRef } from 'react';
import { Gathering, GatheringPendingEdit, GatheringStatus, DragItemTypes } from '../../../../types';
import { useDrag } from 'react-dnd';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PersonIcon from '@mui/icons-material/Person';
import GroupIcon from '@mui/icons-material/Group';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import EditNoteIcon from '@mui/icons-material/EditNote';
import { format } from 'date-fns';
import {
  PENDING_EDIT_TONE_PALETTE,
  describePendingEditStatus
} from '../../utils/pendingEdit';
import { richTextToPlainText } from '../../../../utils/richText';

interface GatheringCardProps {
  gathering: Gathering;
  onUpdateStatus: (gatheringId: string, newStatus: GatheringStatus) => void;
  onViewDetails?: (gatheringId: string) => void;
  /**
   * A staged host edit awaiting review, if any (ENG-1097). Passed in rather
   * than read off `gathering` because the listing endpoint never populates it —
   * see usePendingEditSweep.
   */
  pendingEdit?: GatheringPendingEdit | null;
}

const GatheringCard: React.FC<GatheringCardProps> = ({
  gathering,
  onUpdateStatus,
  onViewDetails,
  pendingEdit
}) => {
  // Create a ref that we'll pass to both useDrag and the div
  const cardRef = useRef<HTMLDivElement>(null);
  
  // Add drag functionality
  const [{ isDragging }, drag] = useDrag({
    type: DragItemTypes.GATHERING,
    item: { 
      id: gathering.id, 
      currentStatus: gathering.status 
    },
    collect: (monitor) => ({
      isDragging: !!monitor.isDragging()
    })
  });
  
  // Connect the drag to our ref
  drag(cardRef);

  // Format time for display
  const formatTime = (timeString: string): string => {
    // Check if the time already includes AM/PM
    if (timeString.includes('AM') || timeString.includes('PM')) {
      return timeString;
    }
    
    // If not, assume timeString is in format "HH:MM:SS"
    const [hours, minutes] = timeString.split(':');
    const hour = parseInt(hours, 10);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const hour12 = hour % 12 || 12;
    return `${hour12}:${minutes} ${ampm}`;
  };

  // Format ID to be shorter and more readable
  const formatId = (id: string): string => {
    return id; // Show the full ID
  };

  // Format date for display without changing the date itself
  const formatDate = (dateString: string): string => {
    // Parse the date string manually (format: YYYY-MM-DD)
    const [year, month, day] = dateString.split('-');
    
    // Convert month number to month name
    const monthNames = [
      'January', 'February', 'March', 'April', 'May', 'June', 
      'July', 'August', 'September', 'October', 'November', 'December'
    ];
    const monthName = monthNames[parseInt(month) - 1]; // Subtract 1 as months are 0-indexed in array
    
    // Get day of week
    const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    // Create date object but extract only the day of week
    const dayOfWeek = new Date(`${year}-${month}-${day}T12:00:00Z`).getUTCDay();
    const dayName = dayNames[dayOfWeek];
    
    // Remove leading zero from day if present
    const dayNum = parseInt(day).toString();
    
    // Format as "Fri, March 22, 2025"
    return `${dayName}, ${monthName} ${dayNum}, ${year}`;
  };

  // Truncate description to show only the first two lines
  const truncateDescription = (description: string): string => {
    if (!description) return '';
    
    // Split by newlines first
    const lines = description.split('\n');
    if (lines.length > 2) {
      return lines.slice(0, 2).join('\n') + '...';
    }
    
    // If there aren't multiple lines, limit by characters
    if (description.length > 120) {
      return description.substring(0, 120) + '...';
    }
    
    return description;
  };

  // Handle card click to view details
  const handleCardClick = () => {
    if (onViewDetails) {
      onViewDetails(gathering.id);
    }
  };

  // A staged edit deliberately leaves the gathering Published, so this badge is
  // the only signal on the board that a host is waiting on a decision.
  const pendingEditStatus = pendingEdit
    ? describePendingEditStatus(pendingEdit.status)
    : null;
  // Toned by status for the same reason the review panel is: an amber badge on
  // an expired or already-decided edit reads as "action needed" when no action
  // is possible. The label text carries the status, so the colour is
  // reinforcement rather than the only signal.
  const pendingEditTone = pendingEditStatus
    ? PENDING_EDIT_TONE_PALETTE[pendingEditStatus.tone]
    : null;
  const pendingEditFieldCount = pendingEdit
    ? Object.keys(pendingEdit.changes || {}).length
    : 0;

  return (
    <div 
      ref={cardRef}
      className={`gathering-card ${isDragging ? 'is-dragging' : ''}`}
      onClick={handleCardClick}
      style={{ 
        opacity: isDragging ? 0.5 : 1,
        cursor: 'grab'
      }}
    >
      <div className="gathering-card-header">
        <h4 className="gathering-title">{gathering.title}</h4>
        {gathering.image_url && (
          <div className="gathering-image">
            <img src={gathering.image_url} alt={gathering.title} />
          </div>
        )}
      </div>
      <div className="gathering-card-content">
        <div className="gathering-details">
          {pendingEditStatus && pendingEditTone && (
            <div
              title={`${pendingEditStatus.label}: ${pendingEditStatus.description}`}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                backgroundColor: pendingEditTone.badgeBg,
                border: `1px solid ${pendingEditTone.accent}`,
                borderRadius: '4px',
                padding: '4px 8px',
                marginBottom: '8px',
                fontSize: '0.78rem',
                color: pendingEditTone.accent,
                fontWeight: 600
              }}
            >
              <EditNoteIcon style={{ color: pendingEditTone.accent, fontSize: '1rem' }} />
              <span>
                {pendingEditStatus.label}
                {pendingEditFieldCount > 0 &&
                  ` · ${pendingEditFieldCount} ${
                    pendingEditFieldCount === 1 ? 'field' : 'fields'
                  }`}
              </span>
            </div>
          )}

          <div className="gathering-date">
            {formatDate(gathering.date)}
          </div>
          <div className="gathering-time">
            {formatTime(gathering.start_time)} - {formatTime(gathering.end_time)}
          </div>
          
          {gathering.created_at && (
            <div style={{ 
              fontSize: '0.75rem', 
              color: '#888', 
              marginTop: '6px',
              fontStyle: 'italic'
            }}>
              Submitted: {format(new Date(gathering.created_at), 'MMM d, yyyy')}
            </div>
          )}
          
          <div className="gathering-description" style={{
            margin: '8px 0',
            fontSize: '0.85rem',
            color: '#555',
            lineHeight: '1.4',
            maxHeight: '2.8em',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical'
          }}>
            {truncateDescription(richTextToPlainText(gathering.description))}
          </div>
          
          <div className="gathering-badges" style={{ 
            display: 'flex', 
            gap: '8px', 
            marginTop: '10px',
            flexWrap: 'wrap',
            justifyContent: 'space-between'
          }}>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              {gathering.space && (
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '4px', 
                  backgroundColor: '#f5f5f5',
                  padding: '4px 8px',
                  borderRadius: '4px',
                  border: '1px solid #e0e0e0',
                  fontSize: '0.8rem'
                }}>
                  <LocationOnIcon style={{ color: '#2196f3', fontSize: '0.9rem' }} />
                  <span>{gathering.space.name}</span>
                </div>
              )}
              
              {gathering.owner && (
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '4px', 
                  backgroundColor: '#f5f5f5',
                  padding: '4px 8px',
                  borderRadius: '4px',
                  border: '1px solid #e0e0e0',
                  fontSize: '0.8rem'
                }}>
                  <PersonIcon style={{ color: '#2196f3', fontSize: '0.9rem' }} />
                  <span>{gathering.owner.display_name}</span>
                </div>
              )}
            </div>
            
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '4px', 
              backgroundColor: '#f5f5f5',
              padding: '4px 8px',
              borderRadius: '4px',
              border: '1px solid #e0e0e0',
              fontSize: '0.8rem',
              marginLeft: 'auto'
            }}>
              <GroupIcon style={{ color: '#2196f3', fontSize: '0.9rem' }} />
              <span>{gathering.event_size}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GatheringCard;